import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMonitor, FiWifi, FiSettings, FiArrowLeft, FiCheck, FiPlus, FiMinus, FiRefreshCw, FiSearch, FiEye } = FiIcons;

const SyncSetup = () => {
  const navigate = useNavigate();
  const [groupId, setGroupId] = useState('');
  const [screenIndex, setScreenIndex] = useState(1);
  const [totalScreens, setTotalScreens] = useState(3);
  const [orientation, setOrientation] = useState('horizontal');
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [discoveredGroups, setDiscoveredGroups] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [pairingCode, setPairingCode] = useState('');
  const [showPairingInput, setShowPairingInput] = useState(false);
  const [language, setLanguage] = useState('en');

  const translations = {
    en: {
      title: 'Multi-Screen Setup',
      subtitle: 'Configure synchronized video wall',
      group: 'Sync Group',
      groupId: 'Group ID',
      groupIdPlaceholder: 'Enter group ID or discover',
      discover: 'Discover Available Groups',
      discovering: 'Discovering...',
      availableGroups: 'Available Groups:',
      online: 'online',
      screenConfig: 'Screen Configuration',
      screenPosition: 'This Screen\'s Position',
      screenOf: 'Screen',
      of: 'of',
      totalScreens: 'Total Screens in Group',
      screens: 'Screens',
      orientation: 'Orientation',
      horizontal: 'Horizontal',
      vertical: 'Vertical',
      advanced: 'Advanced Options',
      syncTolerance: 'Sync Tolerance (ms)',
      masterScreen: 'Master Screen',
      preview: 'Screen Configuration Preview',
      connection: 'Connection Information',
      network: 'Network',
      connected: 'Connected',
      localIp: 'Local IP',
      syncProtocol: 'Sync Protocol',
      timeSync: 'Time Sync',
      connect: 'Connect to Sync Group',
      back: 'Back to Home',
      pairingMode: 'Pairing Mode',
      enterPairingCode: 'Enter Pairing Code',
      pairingCodePlaceholder: 'Enter 6-character pairing code',
      pair: 'Pair Screen'
    },
    de: {
      title: 'Multi-Screen-Einrichtung',
      subtitle: 'Konfigurieren Sie synchronisierte Videowand',
      group: 'Sync-Gruppe',
      groupId: 'Gruppen-ID',
      groupIdPlaceholder: 'Gruppen-ID eingeben oder entdecken',
      discover: 'Verfügbare Gruppen suchen',
      discovering: 'Suche läuft...',
      availableGroups: 'Verfügbare Gruppen:',
      online: 'online',
      screenConfig: 'Bildschirmkonfiguration',
      screenPosition: 'Position dieses Bildschirms',
      screenOf: 'Bildschirm',
      of: 'von',
      totalScreens: 'Gesamtanzahl Bildschirme in Gruppe',
      screens: 'Bildschirme',
      orientation: 'Ausrichtung',
      horizontal: 'Horizontal',
      vertical: 'Vertikal',
      advanced: 'Erweiterte Optionen',
      syncTolerance: 'Synchronisationstoleranz (ms)',
      masterScreen: 'Master-Bildschirm',
      preview: 'Bildschirmkonfiguration Vorschau',
      connection: 'Verbindungsinformationen',
      network: 'Netzwerk',
      connected: 'Verbunden',
      localIp: 'Lokale IP',
      syncProtocol: 'Sync-Protokoll',
      timeSync: 'Zeitsynchronisation',
      connect: 'Mit Sync-Gruppe verbinden',
      back: 'Zurück zum Startbildschirm',
      pairingMode: 'Kopplungsmodus',
      enterPairingCode: 'Kopplungscode eingeben',
      pairingCodePlaceholder: '6-stelligen Kopplungscode eingeben',
      pair: 'Bildschirm koppeln'
    }
  };

  const t = translations[language];

  // Simulate network discovery
  useEffect(() => {
    if (isDiscovering) {
      const timer = setTimeout(() => {
        setDiscoveredGroups([
          { id: 'lobby-wall', name: 'Lobby Video Wall', screens: 3, online: 2 },
          { id: 'conference-room', name: 'Conference Room', screens: 2, online: 0 }
        ]);
        setIsDiscovering(false);
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [isDiscovering]);

  const handleStartDiscovery = () => {
    setIsDiscovering(true);
    setDiscoveredGroups([]);
  };

  const handleJoinGroup = (group) => {
    setGroupId(group.id);
    setTotalScreens(group.screens);
  };

  const handleConnect = () => {
    if (showPairingInput) {
      if (!pairingCode || pairingCode.length < 6) {
        return;
      }
      
      // In a real implementation, we would validate the pairing code against the backend
      // For now, we'll navigate directly to the sync player
      navigate(`/tv/sync/paired-screen/${pairingCode}`);
    } else {
      if (!groupId || screenIndex < 1 || screenIndex > totalScreens) {
        return;
      }
      
      // Navigate to the multi-screen sync player with the appropriate parameters
      navigate(`/tv/sync/${groupId}/${screenIndex}`);
    }
  };

  const incrementScreenIndex = () => {
    if (screenIndex < totalScreens) {
      setScreenIndex(screenIndex + 1);
    }
  };

  const decrementScreenIndex = () => {
    if (screenIndex > 1) {
      setScreenIndex(screenIndex - 1);
    }
  };

  const incrementTotalScreens = () => {
    setTotalScreens(totalScreens + 1);
  };

  const decrementTotalScreens = () => {
    if (totalScreens > 2) {
      setTotalScreens(totalScreens - 1);
      if (screenIndex > totalScreens - 1) {
        setScreenIndex(totalScreens - 1);
      }
    }
  };

  return (
    <div className="min-h-screen px-8 py-12">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.6 }} 
          className="flex items-center justify-between mb-8"
        >
          <div className="flex items-center space-x-4">
            <Link 
              to="/tv" 
              className="p-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl hover:bg-white/20 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} className="text-white" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-white">{t.title}</h1>
              <p className="text-white/70">{t.subtitle}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="relative">
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="px-3 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
              >
                <option value="en">English</option>
                <option value="de">Deutsch</option>
              </select>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-400 rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiMonitor} className="text-white text-xl" />
            </div>
          </div>
        </motion.div>

        {/* Connection Mode Selection */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.6, delay: 0.05 }} 
          className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6 mb-6"
        >
          <h2 className="text-xl font-semibold text-white mb-4">{t.pairingMode}</h2>
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => setShowPairingInput(false)}
              className={`p-4 rounded-xl border ${!showPairingInput ? 'bg-purple-500/30 border-purple-400/50' : 'bg-white/10 border-white/20 hover:bg-white/20'}`}
            >
              <SafeIcon icon={FiSearch} className="mx-auto text-xl mb-2 text-white" />
              <p className="text-white font-medium">Group ID</p>
              <p className="text-white/60 text-xs">Connect by group ID and position</p>
            </button>
            <button
              onClick={() => setShowPairingInput(true)}
              className={`p-4 rounded-xl border ${showPairingInput ? 'bg-purple-500/30 border-purple-400/50' : 'bg-white/10 border-white/20 hover:bg-white/20'}`}
            >
              <SafeIcon icon={FiEye} className="mx-auto text-xl mb-2 text-white" />
              <p className="text-white font-medium">Pairing Code</p>
              <p className="text-white/60 text-xs">Connect using a specific pairing code</p>
            </button>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Configuration Panel */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }} 
            animate={{ opacity: 1, x: 0 }} 
            transition={{ duration: 0.6, delay: 0.1 }} 
            className="space-y-6"
          >
            {showPairingInput ? (
              /* Pairing Code Input */
              <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
                <h2 className="text-xl font-semibold text-white mb-6 flex items-center space-x-2">
                  <SafeIcon icon={FiWifi} />
                  <span>{t.enterPairingCode}</span>
                </h2>
                <div className="space-y-4">
                  <div>
                    <input 
                      type="text" 
                      value={pairingCode} 
                      onChange={(e) => setPairingCode(e.target.value.toUpperCase())} 
                      placeholder={t.pairingCodePlaceholder}
                      className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white text-center tracking-wider placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-400"
                      maxLength={6}
                    />
                  </div>
                  <div className="bg-white/5 rounded-lg p-4">
                    <p className="text-white/70 text-sm">
                      Enter the pairing code displayed on the dashboard for the specific screen you want to connect to.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Group Setup */}
                <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
                  <h2 className="text-xl font-semibold text-white mb-6 flex items-center space-x-2">
                    <SafeIcon icon={FiWifi} />
                    <span>{t.group}</span>
                  </h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        {t.groupId}
                      </label>
                      <input 
                        type="text" 
                        value={groupId} 
                        onChange={(e) => setGroupId(e.target.value)} 
                        placeholder={t.groupIdPlaceholder}
                        className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-400"
                      />
                    </div>
                    
                    <div>
                      <button
                        onClick={handleStartDiscovery}
                        disabled={isDiscovering}
                        className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-white/10 border border-white/20 text-white rounded-xl hover:bg-white/20 transition-colors disabled:opacity-50"
                      >
                        {isDiscovering ? (
                          <>
                            <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                            <span>{t.discovering}</span>
                          </>
                        ) : (
                          <>
                            <SafeIcon icon={FiRefreshCw} />
                            <span>{t.discover}</span>
                          </>
                        )}
                      </button>
                    </div>
                    
                    {/* Discovered Groups */}
                    {discoveredGroups.length > 0 && (
                      <div className="space-y-2 mt-4">
                        <p className="text-white/70 text-sm">{t.availableGroups}</p>
                        {discoveredGroups.map((group) => (
                          <div 
                            key={group.id}
                            onClick={() => handleJoinGroup(group)}
                            className={`p-3 rounded-lg cursor-pointer transition-colors flex items-center justify-between ${
                              groupId === group.id 
                                ? 'bg-purple-500/30 border border-purple-400/50' 
                                : 'bg-white/10 hover:bg-white/20 border border-white/10'
                            }`}
                          >
                            <div>
                              <p className="text-white font-medium">{group.name}</p>
                              <p className="text-white/60 text-sm">{group.screens} {t.screens}</p>
                            </div>
                            <div className="flex items-center space-x-2">
                              <div className={`w-3 h-3 rounded-full ${group.online > 0 ? 'bg-green-400' : 'bg-red-400'}`}></div>
                              <span className="text-white/70 text-sm">{group.online}/{group.screens} {t.online}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Screen Configuration */}
                <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
                  <h2 className="text-xl font-semibold text-white mb-6 flex items-center space-x-2">
                    <SafeIcon icon={FiMonitor} />
                    <span>{t.screenConfig}</span>
                  </h2>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        {t.screenPosition}
                      </label>
                      <div className="flex items-center">
                        <button 
                          onClick={decrementScreenIndex}
                          disabled={screenIndex <= 1}
                          className="p-2 bg-white/10 rounded-l-xl border border-white/20 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <SafeIcon icon={FiMinus} />
                        </button>
                        <div className="flex-1 py-2 px-4 bg-white/5 border-t border-b border-white/20 text-white text-center">
                          {t.screenOf} {screenIndex} {t.of} {totalScreens}
                        </div>
                        <button 
                          onClick={incrementScreenIndex}
                          disabled={screenIndex >= totalScreens}
                          className="p-2 bg-white/10 rounded-r-xl border border-white/20 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <SafeIcon icon={FiPlus} />
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        {t.totalScreens}
                      </label>
                      <div className="flex items-center">
                        <button 
                          onClick={decrementTotalScreens}
                          disabled={totalScreens <= 2}
                          className="p-2 bg-white/10 rounded-l-xl border border-white/20 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <SafeIcon icon={FiMinus} />
                        </button>
                        <div className="flex-1 py-2 px-4 bg-white/5 border-t border-b border-white/20 text-white text-center">
                          {totalScreens} {t.screens}
                        </div>
                        <button 
                          onClick={incrementTotalScreens}
                          className="p-2 bg-white/10 rounded-r-xl border border-white/20 text-white"
                        >
                          <SafeIcon icon={FiPlus} />
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        {t.orientation}
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        <button
                          onClick={() => setOrientation('horizontal')}
                          className={`p-3 rounded-xl border text-center ${
                            orientation === 'horizontal'
                              ? 'bg-purple-500/30 border-purple-400/50 text-white'
                              : 'bg-white/10 border-white/20 text-white/70 hover:bg-white/20'
                          }`}
                        >
                          {t.horizontal}
                        </button>
                        <button
                          onClick={() => setOrientation('vertical')}
                          className={`p-3 rounded-xl border text-center ${
                            orientation === 'vertical'
                              ? 'bg-purple-500/30 border-purple-400/50 text-white'
                              : 'bg-white/10 border-white/20 text-white/70 hover:bg-white/20'
                          }`}
                        >
                          {t.vertical}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Advanced Options */}
                <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
                  <button
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="flex items-center justify-between w-full text-left"
                  >
                    <h2 className="text-xl font-semibold text-white flex items-center space-x-2">
                      <SafeIcon icon={FiSettings} />
                      <span>{t.advanced}</span>
                    </h2>
                    <SafeIcon 
                      icon={showAdvanced ? FiMinus : FiPlus} 
                      className="text-white/70" 
                    />
                  </button>
                  
                  {showAdvanced && (
                    <div className="mt-6 space-y-4">
                      <div>
                        <label className="block text-white/80 text-sm font-medium mb-2">
                          {t.syncTolerance}
                        </label>
                        <input 
                          type="number" 
                          defaultValue={50}
                          min={10}
                          max={500}
                          className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-400"
                        />
                        <p className="text-white/50 text-xs mt-1">Lower values require better network conditions</p>
                      </div>
                      
                      <div>
                        <label className="block text-white/80 text-sm font-medium mb-2">
                          {t.masterScreen}
                        </label>
                        <select 
                          defaultValue={1}
                          className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                        >
                          {Array.from({ length: totalScreens }, (_, i) => (
                            <option key={i} value={i+1}>{t.screenOf} {i+1}</option>
                          ))}
                        </select>
                        <p className="text-white/50 text-xs mt-1">Controls synchronization and plays audio</p>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </motion.div>
          
          {/* Preview & Actions */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }} 
            animate={{ opacity: 1, x: 0 }} 
            transition={{ duration: 0.6, delay: 0.2 }} 
            className="space-y-6"
          >
            {/* Preview */}
            {!showPairingInput && (
              <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
                <h2 className="text-xl font-semibold text-white mb-6">{t.preview}</h2>
                
                <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 flex items-center justify-center">
                  <div className={`grid ${orientation === 'horizontal' ? 'grid-cols-' + totalScreens : 'grid-rows-' + totalScreens} gap-2`}>
                    {Array.from({ length: totalScreens }, (_, i) => (
                      <div 
                        key={i} 
                        className={`
                          ${orientation === 'horizontal' 
                            ? 'w-16 h-32' 
                            : 'w-32 h-16'
                          } 
                          ${i+1 === screenIndex 
                            ? 'bg-purple-500/50 border-2 border-purple-400' 
                            : 'bg-white/10 border border-white/30'
                          } 
                          rounded-lg flex items-center justify-center
                        `}
                      >
                        <span className="text-white font-medium">{i+1}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="mt-4 p-4 bg-white/5 rounded-xl">
                  <p className="text-white/80 text-sm">
                    This screen will display position {screenIndex} of {totalScreens} in the video wall.
                  </p>
                  {screenIndex === 1 && orientation === 'horizontal' && (
                    <p className="text-white/60 text-sm mt-2">
                      This is the leftmost screen in the horizontal arrangement.
                    </p>
                  )}
                  {screenIndex === totalScreens && orientation === 'horizontal' && (
                    <p className="text-white/60 text-sm mt-2">
                      This is the rightmost screen in the horizontal arrangement.
                    </p>
                  )}
                  {screenIndex === 1 && orientation === 'vertical' && (
                    <p className="text-white/60 text-sm mt-2">
                      This is the top screen in the vertical arrangement.
                    </p>
                  )}
                  {screenIndex === totalScreens && orientation === 'vertical' && (
                    <p className="text-white/60 text-sm mt-2">
                      This is the bottom screen in the vertical arrangement.
                    </p>
                  )}
                </div>
              </div>
            )}
            
            {/* System Info */}
            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
              <h2 className="text-xl font-semibold text-white mb-6">{t.connection}</h2>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-white/70">{t.network}</span>
                  <span className="text-green-400 flex items-center space-x-1">
                    <SafeIcon icon={FiWifi} className="text-sm" />
                    <span>{t.connected}</span>
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/70">{t.localIp}</span>
                  <span className="text-white">192.168.1.45</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/70">{t.syncProtocol}</span>
                  <span className="text-white">WebRTC + NTP</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/70">{t.timeSync}</span>
                  <span className="text-green-400">±2ms</span>
                </div>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="space-y-4">
              <button 
                onClick={handleConnect}
                disabled={showPairingInput ? (!pairingCode || pairingCode.length < 6) : (!groupId || screenIndex < 1 || screenIndex > totalScreens)}
                className="w-full flex items-center justify-center space-x-2 px-6 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <SafeIcon icon={FiCheck} />
                <span>{showPairingInput ? t.pair : t.connect}</span>
              </button>
              <Link 
                to="/tv"
                className="w-full flex items-center justify-center space-x-2 px-6 py-4 bg-white/10 border border-white/20 text-white rounded-xl hover:bg-white/20 transition-colors"
              >
                <SafeIcon icon={FiArrowLeft} />
                <span>{t.back}</span>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default SyncSetup;